import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';

import AsyncSelect from 'components/AsyncSelect';
import AsyncSelectItem from 'components/AsyncSelectItem';
import BoxBody from 'components/BoxBody';
import Label from 'components/Label';
import Text from 'components/Text';
import { CoachRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import GetCoachesContainer from 'coaching/containers/GetCoachesContainer';

export type Props = {
  onSelectCoach: (coach?: CoachRow) => void;
  validStatuses: string[];
};

export default function CoachingCoordinatorCoachSelect(props: Props) {
  const [state, setState] = useState({ nameQuery: '' });

  const onQuery = (nameQuery: string) => {
    setState({ nameQuery: nameQuery });
  };

  const queryIsEmpty = state.nameQuery.trim() === '';
  return (
    <GetCoachesContainer query={state.nameQuery} skipQuery={queryIsEmpty}>
      {({ coaches, loading }) => {
        return (
          <CoachSelectContent
            {...props}
            loading={loading}
            coaches={coaches ?? []}
            onQueryChanged={onQuery}
          />
        );
      }}
    </GetCoachesContainer>
  );
}

function CoachSelectContent(
  props: Props & {
    loading: boolean;
    coaches: CoachRow[];
    onQueryChanged: (nameQuery: string) => void;
  }
) {
  const setCoachesCallback = useRef<((promise: Promise<React.ReactElement[]>) => void)[]>();
  const coachQueryState = useRef<{
    loadingState: boolean;
    previousLoadingState: boolean;
    coaches: CoachRow[];
    previousCoaches: CoachRow[];
  }>({ loadingState: false, previousLoadingState: false, previousCoaches: [], coaches: [] });
  /* React-Apollo has some callbacks to get notified when a request has completed, but they don't always work,
   * so instead we keep track of the query result values, and when they change, we know that the request has finished.
   * We also need keep track of the loading  */
  useEffect(() => {
    const queryState = coachQueryState.current;
    const loadingHasFinished = !queryState.loadingState && queryState.previousLoadingState;
    if (
      !haveSameCoaches(coachQueryState.current.coaches, coachQueryState.current.previousCoaches) ||
      loadingHasFinished
    ) {
      updateCoaches(coachQueryState.current.coaches ?? []);
    }
  });
  coachQueryState.current = {
    previousLoadingState: coachQueryState.current.loadingState,
    loadingState: props.loading,
    coaches: props.coaches ?? [],
    previousCoaches: coachQueryState.current.coaches,
  };

  function updateCoaches(coaches: CoachRow[]) {
    const callbacks = setCoachesCallback.current;
    setCoachesCallback.current = [];
    const renderedItems = Promise.resolve(renderCoachItems(coaches ?? []));
    callbacks?.forEach(callback => callback(renderedItems));
  }

  const renderCoachItems = (coaches: CoachRow[]) => {
    const activeCoaches = (coaches ?? []).filter(coach =>
      props.validStatuses.includes(coach.status)
    );
    return (
      activeCoaches.map(coach => (
        <AsyncSelectItem title={coach.name} id={coach.uuid}>
          <BoxBody size="xxsmall">
            <Text align="left">{coach.name}</Text>
          </BoxBody>
        </AsyncSelectItem>
      )) ?? []
    );
  };

  const searchCoaches = debounce((input: string) => {
    props.onQueryChanged(input);
  }, 300);
  const onCoachQueryUpdated = (
    input: string,
    callback: (promise: Promise<React.ReactElement[]>) => void
  ) => {
    /* the AsyncSelect component keeps count of how many outstanding(ie, uncalled) callbacks it has created,
     and uses it to show the loading status when the count is greater than 0. When we get a coach list reponse, we need to call every
      callback we received, not just the last one, so the callback count always goes back to 0
     */
    const currentCallbacks = setCoachesCallback.current ?? [];
    setCoachesCallback.current = [...currentCallbacks, callback];
    searchCoaches(input);
  };

  function handleOnSelect(coachItem: React.ReactElement) {
    props.onSelectCoach(
      coachQueryState.current.coaches.find(coach => coach.uuid === coachItem.props.id)
    );
  }

  return (
    <React.Fragment>
      <Label>Coach</Label>
      <AsyncSelect
        data-test="coach-async-select"
        initialItems={[]}
        infiniteLoad
        noResults={<>No coaches found</>}
        placeholder="Search for a coach"
        searchCallback={onCoachQueryUpdated}
        onSelect={handleOnSelect}
      />
    </React.Fragment>
  );
}

/** Compares two arrays of coaches and returns true if they have the same coaches **/
function haveSameCoaches(coaches: CoachRow[], otherCoaches: CoachRow[]) {
  const coachesUuids = coaches.map(coach => coach.uuid).join(' ');
  const otherCoachesUuids = otherCoaches.map(coach => coach.uuid).join(' ');
  return coachesUuids === otherCoachesUuids;
}
